import { useAuthenticationStore } from '@scoring/state';
import z from 'zod';
import { type ApiError, type ApiResult } from '..';

const request = async <TResponse, TPayload>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'QUERY',
    payload?: TPayload,
    schema?: z.ZodSchema<TResponse>,
    options: { payloadIsFormData: boolean; responseType: 'json' | 'blob' } = {
        payloadIsFormData: false,
        responseType: 'json',
    },
    abort?: AbortSignal
): Promise<ApiResult<TResponse>> => {
    // This app is being served at {address}/manager, we need to call {address}/api/... to reach the correct API

    const apiUrl = `/api/${endpoint}`;

    try {
        // Read token directly from zustand store
        const token = useAuthenticationStore.getState().authorization?.token ?? null;
        const headers: HeadersInit = {
            authorization: `bearer ${token}`,
        };

        if (payload && !options.payloadIsFormData) {
            headers['content-type'] = 'application/json';
        }

        let body: BodyInit | null = null;
        if (payload) {
            if (!options.payloadIsFormData) body = JSON.stringify(payload);
            else body = payload as BodyInit;
        }

        abort ??= AbortSignal.timeout(5000); //Default timeout of 5 seconds for all requests that dont provide their own abort signal

        const response = await fetch(apiUrl, {
            method: method,
            signal: abort,
            headers: headers,
            body: body,
        });

        //await new Promise((resolve) => setTimeout(resolve, 3000)); //Fake delay for testing loading states

        let responsedata: unknown;
        if (options.responseType === 'blob') {
            responsedata = await response.blob();
        } else {
            const text = await response.text();
            responsedata = text ? JSON.parse(text) : undefined;
        }
        if (response.ok) {
            const correlationId = response.headers.get('X-Correlation-ID') ?? '';
            if (schema) {
                const parseResult = schema.safeParse(responsedata);
                if (parseResult.success) {
                    return {
                        data: parseResult.data,
                        status: response.status,
                        statusText: response.statusText,
                        isSuccess: true,
                        correlationId: correlationId,
                    };
                } else {
                    console.warn('Validation error, returning data anyway', parseResult.error);
                    return {
                        data: responsedata as TResponse,
                        status: response.status,
                        statusText: response.statusText,
                        isSuccess: true,
                        correlationId: correlationId,
                    };
                }
            } else {
                console.warn('Request hasnt been migrated to zod, no schema passed!', responsedata);
                return {
                    data: responsedata as TResponse,
                    status: response.status,
                    statusText: response.statusText,
                    isSuccess: true,
                    correlationId: correlationId,
                };
            }
        } else {
            throw {
                data: transformError(responsedata, response),
                status: response.status,
                statusText: response.statusText,
                isSuccess: false,
            };
        }
    } catch (e) {
        if (isApiErrorResult(e)) {
            throw e;
        } else if (typeof e === 'string') {
            throw createError(e);
        } else if (e instanceof Error) {
            throw createError(e.message);
        } else {
            throw createError('Unknown error occurred');
        }
    }
};

const isApiErrorResult = (value: unknown): value is ApiResult<unknown> & { isSuccess: false } =>
    typeof value === 'object' &&
    value !== null &&
    'isSuccess' in value &&
    (value as { isSuccess?: unknown }).isSuccess === false;

const createError = (message: string, status = 500) => ({
    data: { message, validationErrors: [] },
    status,
    statusText: message,
    isSuccess: false,
});

const transformError = (payload: unknown, response?: Response) => {
    // Handle HTTP Problem details format
    // An empty body is possible (a proxy timeout, a 401 from the gateway) - fall back to the response itself.
    const problemDetails = (payload ?? {}) as {
        type?: string;
        title?: string;
        status?: number;
        traceId?: string;
        detail?: string;
        correlationId?: string;
        requestId?: string;
        errors?: Record<string, string[]>;
    };

    const status = problemDetails.status ?? response?.status ?? 0;

    // On a 5xx the title is a generic 'An unexpected error occurred' and the detail carries the substance;
    // on a 4xx it is the other way round.
    const message =
        (status >= 500
            ? problemDetails.detail || problemDetails.title
            : problemDetails.title || problemDetails.detail) || 'An error occurred';

    // The server also echoes the id in a header, which survives responses that carry no problem details body.
    const correlationId = problemDetails.correlationId ?? response?.headers.get('X-Correlation-Id') ?? undefined;
    const requestId = problemDetails.requestId ?? response?.headers.get('X-Request-Id') ?? undefined;

    const error: ApiError = {
        message,
        validationErrors: [],
        // Add additional context for debugging
        ...(problemDetails.detail && { detail: problemDetails.detail }),
        ...(problemDetails.traceId && { traceId: problemDetails.traceId }),
        ...(problemDetails.type && { problemType: problemDetails.type }),
        ...(correlationId && { correlationId }),
        ...(requestId && { requestId }),
    };

    // Handle validation errors if present
    if (problemDetails.errors) {
        for (const field in problemDetails.errors) {
            const fieldErrors = problemDetails.errors[field];
            error.validationErrors.push({ field: field, errors: fieldErrors });
        }
    }

    return error;
};

export const get = <TResponse, TPayload = undefined>(
    endpoint: string,
    payload?: TPayload,
    schema?: z.ZodSchema<TResponse>,
    abort?: AbortSignal
): Promise<ApiResult<TResponse>> =>
    request(endpoint, 'GET', payload, schema, { payloadIsFormData: false, responseType: 'json' }, abort);

export const post = <TResponse, TPayload = undefined>(
    endpoint: string,
    payload?: TPayload,
    schema?: z.ZodSchema<TResponse>,
    abort?: AbortSignal
): Promise<ApiResult<TResponse>> =>
    request(endpoint, 'POST', payload, schema, { payloadIsFormData: false, responseType: 'json' }, abort);

// A read that carries a body, via the HTTP QUERY method.
export const query = <TResponse, TPayload = undefined>(
    endpoint: string,
    payload?: TPayload,
    schema?: z.ZodSchema<TResponse>,
    abort?: AbortSignal
): Promise<ApiResult<TResponse>> =>
    request(endpoint, 'QUERY', payload, schema, { payloadIsFormData: false, responseType: 'json' }, abort);

export const del = <TResponse, TPayload = undefined>(
    endpoint: string,
    payload?: TPayload,
    schema?: z.ZodSchema<TResponse>,
    abort?: AbortSignal
): Promise<ApiResult<TResponse>> =>
    request(endpoint, 'DELETE', payload, schema, { payloadIsFormData: false, responseType: 'json' }, abort);

export const put = <TResponse = void, TPayload = undefined>(
    endpoint: string,
    payload?: TPayload,
    schema?: z.ZodSchema<TResponse>,
    abort?: AbortSignal
): Promise<ApiResult<TResponse>> =>
    request(endpoint, 'PUT', payload, schema, { payloadIsFormData: false, responseType: 'json' }, abort);

export const getBlob = <TResponse, TPayload = undefined>(
    endpoint: string,
    payload?: TPayload,
    schema?: z.ZodSchema<TResponse>,
    abort?: AbortSignal
): Promise<ApiResult<TResponse>> => {
    return request(endpoint, 'GET', payload, schema, { payloadIsFormData: false, responseType: 'blob' }, abort);
};

export const putBlob = <TResponse, TPayload = undefined>(
    endpoint: string,
    payload?: TPayload,
    schema?: z.ZodSchema<TResponse>,
    abort?: AbortSignal
): Promise<ApiResult<TResponse>> =>
    request(endpoint, 'PUT', payload, schema, { payloadIsFormData: true, responseType: 'json' }, abort);
